/**
 * Scroll reveal animazioni (stile Sussex Taps): fade-up, stagger, GSAP + ScrollTrigger.
 * Funziona con Lenis (ScrollTrigger.update già collegato in lenis.js).
 * Rispetta prefers-reduced-motion.
 */
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const EXCLUDE_SELECTOR = '#pageHeader, #panelMenu, #pageFooter';
const DEFAULT_ANIMATION = 'fade-up';
const DEFAULT_DURATION = 0.7;
const DEFAULT_DELAY = 0;
const DEFAULT_Y = 48;
const STAGGER = 0.08;

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getAnimationConfig(type) {
    const y = DEFAULT_Y;
    switch (type) {
        case 'fade-up':
            return { from: { opacity: 0, y }, to: { opacity: 1, y: 0 } };
        case 'fade-down':
            return { from: { opacity: 0, y: -y }, to: { opacity: 1, y: 0 } };
        case 'fade-left':
            return { from: { opacity: 0, x: y }, to: { opacity: 1, x: 0 } };
        case 'fade-right':
            return { from: { opacity: 0, x: -y }, to: { opacity: 1, x: 0 } };
        case 'fade':
            return { from: { opacity: 0 }, to: { opacity: 1 } };
        case 'zoom-in':
            return { from: { opacity: 0, scale: 0.92 }, to: { opacity: 1, scale: 1 } };
        default:
            return getAnimationConfig(DEFAULT_ANIMATION);
    }
}

function animateElement(el, options = {}) {
    const type = (el.getAttribute('data-animation') || DEFAULT_ANIMATION).toLowerCase();
    const duration = parseFloat(el.getAttribute('data-animation-duration')) || options.duration || DEFAULT_DURATION;
    const delay = parseFloat(el.getAttribute('data-animation-delay')) || options.delay || DEFAULT_DELAY;
    const { from, to } = getAnimationConfig(type);

    gsap.set(el, { ...from, visibility: 'visible' });
    gsap.to(el, {
        ...to,
        duration,
        delay,
        ease: 'power2.out',
        overwrite: true,
        onComplete: () => {
            el.classList.add('is-animated', 'animation-complete');
            gsap.set(el, { clearProps: 'transform' });
        }
    });
}

function initScrollReveal() {
    if (prefersReducedMotion()) return;

    // Elementi singoli (escludi chi ha data-animation-stagger: anima solo i figli)
    const selector = `[data-animation]:not([data-animation="none"]):not([data-animation-stagger]):not(${EXCLUDE_SELECTOR} [data-animation])`;
    const elements = document.querySelectorAll(selector);

    elements.forEach((el) => {
        if (el.closest(EXCLUDE_SELECTOR)) return;
        if (el.classList.contains('is-animated')) return;

        const duration = parseFloat(el.getAttribute('data-animation-duration')) || DEFAULT_DURATION;
        const delay = parseFloat(el.getAttribute('data-animation-delay')) || DEFAULT_DELAY;
        const type = (el.getAttribute('data-animation') || DEFAULT_ANIMATION).toLowerCase();
        const { from, to } = getAnimationConfig(type);

        gsap.set(el, { ...from, visibility: 'visible' });

        gsap.to(el, {
            ...to,
            duration,
            delay,
            ease: 'power2.out',
            overwrite: true,
            scrollTrigger: {
                trigger: el,
                start: 'top 88%',
                end: 'bottom 12%',
                once: true,
                onEnter: () => {},
            },
            onComplete: () => {
                el.classList.add('is-animated', 'animation-complete');
                gsap.set(el, { clearProps: 'transform' });
            }
        });
    });

    // Stagger: contenitori con data-animation-stagger animano i figli in sequenza
    const staggerContainers = document.querySelectorAll('[data-animation-stagger]:not(' + EXCLUDE_SELECTOR + ' [data-animation-stagger])');
    staggerContainers.forEach((container) => {
        // Il contenitore ha [data-animation] quindi in CSS parte con opacity:0: rendilo subito visibile
        gsap.set(container, { opacity: 1, visibility: 'visible', clearProps: 'transform' });

        const childSelector = container.getAttribute('data-animation-stagger') || '> *';
        const children = container.querySelectorAll(childSelector);
        const staggerVal = parseFloat(container.getAttribute('data-animation-stagger-delay')) || STAGGER;
        const animType = (container.getAttribute('data-animation') || DEFAULT_ANIMATION).toLowerCase();
        const { from, to } = getAnimationConfig(animType);

        children.forEach((child, i) => {
            if (child.classList.contains('is-animated')) return;
            gsap.set(child, { ...from, visibility: 'visible' });
            gsap.to(child, {
                ...to,
                duration: DEFAULT_DURATION,
                delay: i * staggerVal,
                ease: 'power2.out',
                overwrite: true,
                scrollTrigger: {
                    trigger: container,
                    start: 'top 85%',
                    once: true,
                },
                onComplete: () => {
                    child.classList.add('is-animated', 'animation-complete');
                    gsap.set(child, { clearProps: 'transform' });
                }
            });
        });
    });

    // Sezioni full-panel senza data-animation: fade-up di default (stile Sussex)
    const sections = document.querySelectorAll('.page-main > .full-panel:not([data-animation]), .page-main > section:not([data-animation])');
    sections.forEach((section) => {
        if (section.closest(EXCLUDE_SELECTOR)) return;
        if (section.classList.contains('is-animated')) return;
        const { from, to } = getAnimationConfig('fade-up');
        gsap.set(section, { ...from, visibility: 'visible' });
        gsap.to(section, {
            ...to,
            duration: DEFAULT_DURATION,
            ease: 'power2.out',
            overwrite: true,
            scrollTrigger: {
                trigger: section,
                start: 'top 88%',
                once: true,
            },
            onComplete: () => {
                section.classList.add('is-animated', 'animation-complete');
                gsap.set(section, { clearProps: 'transform' });
            }
        });
    });
}

export { initScrollReveal, animateElement, getAnimationConfig };
